import React, { useState, useEffect, useContext, useCallback } from "react";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import { useAuditContext } from "../../contexts/AuditContext";
import { toast } from "react-toastify";

/**
 * FixInvoiceModal
 *
 * Admin-only modal for correcting posted invoice line items.
 * Shows the invoice header read-only, with editable line items below.
 * "Recalculate" previews the new totals without saving.
 * "Save Corrections" writes the changes to invoice_details and
 * recalculates invoice.total_amount / balance_due on the backend.
 *
 * Props:
 *   show        {boolean}  - whether the modal is visible
 *   invoice     {object}   - the invoice row from the Posted Invoices table
 *   onClose     {function} - called when modal closes
 *   onSaved     {function} - called after successful save (to refresh list)
 */

// Mills-based rounding — same helper used across all invoice files
const roundAmount = (val) => Math.round(Math.round(val * 1000) / 10) / 100;

const formatCurrency = (val) => {
  const num = parseFloat(val) || 0;
  const rounded = roundAmount(num);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(rounded);
};

// Timezone-safe date display — splits the string directly
const formatDate = (dateVal) => {
  if (!dateVal) return "-";
  const str = typeof dateVal === "string" ? dateVal : dateVal.toISOString();
  const parts = str.split("T")[0].split("-");
  if (parts.length !== 3) return "-";
  return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

const FixInvoiceModal = ({ show, invoice, onClose, onSaved }) => {
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");
  const [recalculated, setRecalculated] = useState(null); // preview of new totals

  const apiBase = getApiBaseURL();
  const { tenant_id, user } = useContext(AuthContext);
  const { logAuditEvent, PURPOSES } = useAuditContext();

  // ============================================================
  // Load line items when modal opens
  // ============================================================
  const fetchLineItems = useCallback(async () => {
    if (!invoice?.invoice_id || !tenant_id) return;
    setLoading(true);
    setRecalculated(null);

    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] =
        `Open fix modal for invoice ${invoice.invoice_number || invoice.invoice_id}`;
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      const res = await secureAxios.get(
        `${apiBase}/api/invoices/${invoice.invoice_id}/line-items`,
        { params: { tenant_id } }
      );

      // Build editable copies — keep originals for change detection
      const items = (res.data || []).map((item) => ({
        ...item,
        amount_edited: parseFloat(item.amount) || 0,
        rate_applied_edited:
          parseFloat(item.rate_applied) || parseFloat(item.service_rate) || 0,
        dirty: false,
      }));

      setLineItems(items);
    } catch (err) {
      console.error("[FixInvoiceModal] Error loading line items:", err);
      toast.error("Failed to load invoice line items");
    } finally {
      setLoading(false);
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  }, [invoice?.invoice_id, tenant_id, apiBase, PURPOSES.PAYMENT]);

  useEffect(() => {
    if (show) {
      setCorrectionReason("");
      setRecalculated(null);
      fetchLineItems();
    }
  }, [show, fetchLineItems]);

  // ============================================================
  // Edit handlers
  // ============================================================
  const handleAmountChange = (index, value) => {
    setLineItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, amount_edited: value, dirty: true }
          : item
      )
    );
    setRecalculated(null); // clear preview on any edit
  };

  const handleRateChange = (index, value) => {
    setLineItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, rate_applied_edited: value, dirty: true }
          : item
      )
    );
    setRecalculated(null);
  };

  // ============================================================
  // Recalculate preview (client-side only, no save)
  // ============================================================
  const handleRecalculate = () => {
    let subtotalServices = 0;
    let subtotalMileage = 0;

    lineItems.forEach((item) => {
      const amt = parseFloat(item.amount_edited) || 0;
      if (item.log_type === "mileage") {
        subtotalMileage += amt;
      } else {
        subtotalServices += amt;
      }
    });

    const roundedServices = roundAmount(subtotalServices);
    const roundedMileage = roundAmount(subtotalMileage);
    const newTotal = roundAmount(roundedServices + roundedMileage);
    const amountPaid = parseFloat(invoice?.amount_paid) || 0;
    const newBalance = roundAmount(Math.max(0, newTotal - amountPaid));

    setRecalculated({
      subtotal_services: roundedServices,
      subtotal_mileage: roundedMileage,
      total_amount: newTotal,
      balance_due: newBalance,
    });
  };

  // ============================================================
  // Save corrections
  // ============================================================
  const handleSave = async () => {
    if (!correctionReason.trim()) {
      toast.warning("Please enter a reason for the correction before saving.");
      return;
    }

    // Must have recalculated first so admin has seen the new total
    if (!recalculated) {
      toast.warning("Please click Recalculate to review the new totals before saving.");
      return;
    }

    const dirtyItems = lineItems.filter((item) => item.dirty);
    if (dirtyItems.length === 0) {
      toast.info("No changes detected.");
      return;
    }

    setSaving(true);
    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] = correctionReason;
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      const payload = {
        tenant_id,
        correction_reason: correctionReason,
        line_items: lineItems.map((item) => ({
          invoice_job_log_id: item.invoice_job_log_id,
          amount: parseFloat(item.amount_edited) || 0,
          rate_applied: parseFloat(item.rate_applied_edited) || null,
        })),
      };

      const res = await secureAxios.patch(
        `${apiBase}/api/invoices/${invoice.invoice_id}/fix`,
        payload
      );

      await logAuditEvent({
        action: "FIX_INVOICE_SAVED",
        entity_type: "INVOICE",
        entity_id: invoice.invoice_id,
        entity_identifier: `Corrected invoice ${invoice.invoice_number || invoice.invoice_id}`,
        contains_phi: true,
        phi_types: ["consumer_name", "authorization_number", "total_amount"],
        purpose_of_use: PURPOSES.PAYMENT,
        action_status: "SUCCESS",
      });

      toast.success(
        `Invoice corrected. New total: ${formatCurrency(res.data.total_amount)}`
      );

      onSaved && onSaved();
      onClose();
    } catch (err) {
      console.error("[FixInvoiceModal] Error saving corrections:", err);
      toast.error(err.response?.data?.error || "Failed to save corrections");
    } finally {
      setSaving(false);
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  };

  if (!show || !invoice) return null;

  const jobLines = lineItems.filter((i) => i.log_type !== "mileage");
  const mileageLines = lineItems.filter((i) => i.log_type === "mileage");
  const hasChanges = lineItems.some((i) => i.dirty);

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1055 }}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-scrollable"
        style={{ maxWidth: "95vw" }}
      >
        <div className="modal-content">
          {/* ================================================
              HEADER
          ================================================ */}
          <div className="modal-header bg-warning-subtle">
            <div>
              <h5 className="modal-title mb-0">
                <i className="bi bi-pencil-square me-2"></i>
                Fix Posted Invoice
              </h5>
              <small className="text-muted">
                Admin correction — all changes are audit logged
              </small>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={saving}
            />
          </div>

          <div className="modal-body">
            {/* ================================================
                INVOICE HEADER (read-only)
            ================================================ */}
            <div className="card mb-3 border-secondary">
              <div className="card-header bg-light py-2">
                <strong>
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Invoice Header
                </strong>
                <span className="badge bg-secondary ms-2 small">Read Only</span>
              </div>
              <div className="card-body py-2">
                <div className="row g-2 small">
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Invoice #</div>
                    <div className="fw-bold text-primary">
                      {invoice.invoice_number || (
                        <code>{invoice.invoice_id?.slice(0, 8)}</code>
                      )}
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Consumer</div>
                    <div className="fw-bold">{invoice.consumer_name}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Authorization #</div>
                    <div className="fw-bold">{invoice.authorization_number}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Job Coach</div>
                    <div>{invoice.user_name || "-"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Invoice Date</div>
                    <div>{formatDate(invoice.invoice_date)}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Due Date</div>
                    <div>{formatDate(invoice.due_date)}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Status</div>
                    <div>
                      <span className="badge bg-secondary">
                        {(invoice.status || "pending").replace("_", " ").toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Amount Paid</div>
                    <div className="text-success fw-bold">
                      {formatCurrency(invoice.amount_paid)}
                    </div>
                  </div>
                  {/* Current totals */}
                  <div className="col-12">
                    <hr className="my-1" />
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Current Services Total</div>
                    <div>{formatCurrency(invoice.subtotal_services)}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Current Mileage Total</div>
                    <div>{formatCurrency(invoice.subtotal_mileage)}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Current Invoice Total</div>
                    <div className="fw-bold">{formatCurrency(invoice.total_amount)}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted">Current Balance Due</div>
                    <div className="fw-bold text-danger">
                      {formatCurrency(invoice.balance_due ?? invoice.total_amount)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* HIPAA notice */}
            <div className="alert alert-warning py-2 small mb-3">
              <i className="bi bi-shield-exclamation me-1"></i>
              <strong>Admin Action:</strong> Corrections to posted invoices are
              permanently recorded in the audit log. Only fix genuine errors —
              do not use this to circumvent billing processes.
            </div>

            {/* ================================================
                LINE ITEMS (editable)
            ================================================ */}
            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status" />
                <div className="mt-2 small text-muted">Loading line items...</div>
              </div>
            ) : (
              <>
                {/* Job Logs */}
                {jobLines.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">
                      <i className="bi bi-briefcase me-1"></i>
                      Job Log Lines ({jobLines.length})
                      {invoice.flat_rate && (
                        <span className="badge bg-success ms-2">Flat Rate</span>
                      )}
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Date</th>
                            <th>Service</th>
                            <th className="text-end">Hours</th>
                            <th className="text-end">Rate Applied</th>
                            <th className="text-end" style={{ minWidth: "130px" }}>
                              Amount
                              <span className="text-danger ms-1">*</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobLines.map((item, idx) => {
                            const globalIdx = lineItems.indexOf(item);
                            return (
                              <tr
                                key={item.invoice_job_log_id || idx}
                                className={item.dirty ? "table-warning" : ""}
                              >
                                <td className="small">
                                  {formatDate(item.date_of_service)}
                                </td>
                                <td className="small">{item.service_name || "-"}</td>
                                <td className="text-end small">
                                  {parseFloat(item.hours_worked || 0).toFixed(2)}
                                </td>
                                <td className="text-end">
                                  <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    className="form-control form-control-sm text-end"
                                    style={{ width: "90px", display: "inline-block" }}
                                    value={item.rate_applied_edited}
                                    onChange={(e) =>
                                      handleRateChange(globalIdx, e.target.value)
                                    }
                                  />
                                </td>
                                <td className="text-end">
                                  <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    className={`form-control form-control-sm text-end ${
                                      item.dirty ? "border-warning" : ""
                                    }`}
                                    style={{ width: "110px", display: "inline-block" }}
                                    value={item.amount_edited}
                                    onChange={(e) =>
                                      handleAmountChange(globalIdx, e.target.value)
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Mileage Logs */}
                {mileageLines.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-success mb-2">
                      <i className="bi bi-signpost me-1"></i>
                      Mileage Lines ({mileageLines.length})
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Date</th>
                            <th>Reason</th>
                            <th className="text-end">Miles</th>
                            <th className="text-end">Rate Applied</th>
                            <th className="text-end" style={{ minWidth: "130px" }}>
                              Amount
                              <span className="text-danger ms-1">*</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {mileageLines.map((item, idx) => {
                            const globalIdx = lineItems.indexOf(item);
                            return (
                              <tr
                                key={item.invoice_job_log_id || idx}
                                className={item.dirty ? "table-warning" : ""}
                              >
                                <td className="small">
                                  {formatDate(item.trip_date)}
                                </td>
                                <td className="small">
                                  {item.reason_for_travel || "-"}
                                </td>
                                <td className="text-end small">
                                  {parseFloat(item.total_miles || 0).toFixed(1)}
                                </td>
                                <td className="text-end">
                                  <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    className="form-control form-control-sm text-end"
                                    style={{ width: "90px", display: "inline-block" }}
                                    value={item.rate_applied_edited}
                                    onChange={(e) =>
                                      handleRateChange(globalIdx, e.target.value)
                                    }
                                  />
                                </td>
                                <td className="text-end">
                                  <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    className={`form-control form-control-sm text-end ${
                                      item.dirty ? "border-warning" : ""
                                    }`}
                                    style={{ width: "110px", display: "inline-block" }}
                                    value={item.amount_edited}
                                    onChange={(e) =>
                                      handleAmountChange(globalIdx, e.target.value)
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {lineItems.length === 0 && (
                  <div className="alert alert-info">
                    <i className="bi bi-inbox me-2"></i>
                    No line items found for this invoice.
                  </div>
                )}

                {/* ================================================
                    RECALCULATED TOTALS PREVIEW
                ================================================ */}
                {recalculated && (
                  <div className="card mb-3 border-success">
                    <div className="card-header bg-success text-white py-2">
                      <i className="bi bi-calculator me-2"></i>
                      <strong>Recalculated Totals Preview</strong>
                    </div>
                    <div className="card-body py-2">
                      <div className="row g-2 small">
                        <div className="col-6 col-md-3">
                          <div className="text-muted">New Services Subtotal</div>
                          <div className="fw-bold">
                            {formatCurrency(recalculated.subtotal_services)}
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="text-muted">New Mileage Subtotal</div>
                          <div className="fw-bold">
                            {formatCurrency(recalculated.subtotal_mileage)}
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="text-muted">New Invoice Total</div>
                          <div className="fw-bold text-success fs-6">
                            {formatCurrency(recalculated.total_amount)}
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="text-muted">New Balance Due</div>
                          <div className="fw-bold text-danger">
                            {formatCurrency(recalculated.balance_due)}
                          </div>
                        </div>
                      </div>
                      {/* Change delta */}
                      <div className="mt-2 text-muted small">
                        <i className="bi bi-arrow-left-right me-1"></i>
                        Change from current total:{" "}
                        <strong>
                          {formatCurrency(
                            recalculated.total_amount -
                              parseFloat(invoice.total_amount || 0)
                          )}
                        </strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* ================================================
                    CORRECTION REASON (required)
                ================================================ */}
                <div className="mb-3">
                  <label className="form-label small fw-bold">
                    Reason for Correction{" "}
                    <span className="text-danger">*</span>
                    <span className="text-muted fw-normal ms-1">
                      (required for audit trail)
                    </span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="e.g. Correcting mileage rate from $0.725 to $0.815 per billing period change..."
                    value={correctionReason}
                    onChange={(e) => setCorrectionReason(e.target.value)}
                    maxLength={500}
                  />
                  <div className="text-end small text-muted mt-1">
                    {correctionReason.length}/500
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ================================================
              FOOTER
          ================================================ */}
          <div className="modal-footer">
            <div className="me-auto small text-muted">
              <i className="bi bi-info-circle me-1"></i>
              Yellow rows have unsaved changes.
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={handleRecalculate}
              disabled={loading || !hasChanges}
              title="Preview new totals without saving"
            >
              <i className="bi bi-calculator me-1"></i>
              Recalculate
            </button>
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleSave}
              disabled={saving || loading || !hasChanges || !correctionReason.trim() || !recalculated}
              title="Save corrections and update invoice totals"
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-1"></i>
                  Save Corrections
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FixInvoiceModal;